import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAgentEditorApi } from "../services/api/useAgentEditorApi";
import { toast } from "sonner";
import { getErrorMessage } from "@/services/api/apiService";

interface AdminActionState {
  [agentId: string]: {
    isLoading: boolean;
    error: Error | string | null;
    success: string | null;
    scrapeStartedAt?: number | null;
    timestamp?: number | null;
  };
}

interface AdminContextType {
  invalidateAdminAgents: () => Promise<void>;
  // agent panic/recover & rescrape
  panicStates: AdminActionState;
  recoverStates: AdminActionState;
  rescrapeStates: AdminActionState;
  handlePanic: (agentId: string) => Promise<void>;
  handleRecover: (agentId: string) => Promise<void>;
  handleRescrape: (agentId: string) => Promise<void>;
  updateRescrapeState: (
    agentId: string,
    updates: Partial<AdminActionState[string]>,
  ) => void;
  // global panic
  isPanicAllLoading: boolean;
  isPanicAllConfirmationVisible: boolean;
  panicAllError: Error | string | null;
  panicAllSuccess: string | null;
  setIsPanicAllConfirmationVisible: (isVisible: boolean) => void;
  handlePanicAll: () => Promise<void>;
  // global recover
  isRecoverAllLoading: boolean;
  recoverAllError: Error | string | null;
  recoverAllSuccess: string | null;
  handleRecoverAll: () => Promise<void>;
}

const AdminContext = createContext<AdminContextType | null>(null);

export function useAdmin() {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error("useAdmin must be used within an AdminProvider");
  }
  return context;
}

const RESCRAPE_POLLING_INTERVAL = 30000; // 30 seconds

export const AdminProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const queryClient = useQueryClient();
  const agentEditorApi = useAgentEditorApi();

  // Agent panic/recover & rescrape
  const [panicStates, setPanicStates] = useState<AdminActionState>({});
  const [recoverStates, setRecoverStates] = useState<AdminActionState>({});
  const [rescrapeStates, setRescrapeStates] = useState<AdminActionState>({});

  // Global panic all states
  const [isPanicAllLoading, setIsPanicAllLoading] = useState(false);
  const [isPanicAllConfirmationVisible, setIsPanicAllConfirmationVisible] =
    useState(false);
  const [panicAllError, setPanicAllError] = useState<Error | string | null>(
    null,
  );
  const [panicAllSuccess, setPanicAllSuccess] = useState<string | null>(null);

  // Global recover all states
  const [isRecoverAllLoading, setIsRecoverAllLoading] = useState(false);
  const [recoverAllError, setRecoverAllError] = useState<Error | string | null>(
    null,
  );
  const [recoverAllSuccess, setRecoverAllSuccess] = useState<string | null>(
    null,
  );

  const handlePanic = async (agentId: string) => {
    setPanicStates((prev) => ({
      ...prev,
      [agentId]: {
        isLoading: true,
        error: null,
        success: null,
        timestamp: Date.now(),
      },
    }));
    try {
      await agentEditorApi.panicAgent(agentId);
      setPanicStates((prev) => ({
        ...prev,
        [agentId]: {
          isLoading: false,
          error: null,
          success: `Successfully panicked`,
          timestamp: Date.now(),
        },
      }));
      await invalidateAdminAgents();
    } catch (error) {
      setPanicStates((prev) => ({
        ...prev,
        [agentId]: {
          isLoading: false,
          error: error instanceof Error ? error.message : String(error),
          success: null,
          timestamp: Date.now(),
        },
      }));
    }
  };

  const handleRecover = async (agentId: string) => {
    setRecoverStates((prev) => ({
      ...prev,
      [agentId]: {
        isLoading: true,
        error: null,
        success: null,
        timestamp: Date.now(),
      },
    }));
    try {
      await agentEditorApi.recoverAgent(agentId);
      setRecoverStates((prev) => ({
        ...prev,
        [agentId]: {
          isLoading: false,
          error: null,
          success: `Successfully recovered`,
          timestamp: Date.now(),
        },
      }));
      await invalidateAdminAgents();
    } catch (error) {
      setRecoverStates((prev) => ({
        ...prev,
        [agentId]: {
          isLoading: false,
          error: error instanceof Error ? error.message : String(error),
          success: null,
          timestamp: Date.now(),
        },
      }));
    }
  };

  const handleRescrape = async (agentId: string) => {
    setRescrapeStates((prev) => ({
      ...prev,
      [agentId]: {
        isLoading: true,
        error: null,
        success: null,
        scrapeStartedAt: new Date().getTime(),
        timestamp: Date.now(),
      },
    }));

    try {
      await agentEditorApi.rescrapeMenu(agentId);
      // Start polling after initiating rescrape
      setTimeout(() => {
        startPollingIfNeeded();
      }, RESCRAPE_POLLING_INTERVAL);
    } catch (error) {
      setRescrapeStates((prev) => ({
        ...prev,
        [agentId]: {
          isLoading: false,
          error: getErrorMessage(error),
          success: null,
          timestamp: Date.now(),
        },
      }));
    }
  };

  const invalidateAdminAgents = async () => {
    await queryClient.invalidateQueries({ queryKey: ["admin-agents"] });
  };

  const handlePanicAll = async () => {
    setIsPanicAllConfirmationVisible(false);
    setIsPanicAllLoading(true);
    setPanicAllError(null);
    setPanicAllSuccess(null);
    try {
      await agentEditorApi.panicAllAgents();
      setPanicAllSuccess(
        `Successfully panicked all agents (${new Date().toLocaleString()})`,
      );
      await invalidateAdminAgents();
    } catch (error) {
      setPanicAllError(error instanceof Error ? error.message : String(error));
    } finally {
      setIsPanicAllLoading(false);
    }
  };

  const handleRecoverAll = async () => {
    setIsRecoverAllLoading(true);
    setRecoverAllError(null);
    setRecoverAllSuccess(null);
    try {
      await agentEditorApi.recoverAllAgents();
      setRecoverAllSuccess(
        `Successfully recovered all agents (${new Date().toLocaleString()}). Please re-panic the agents that were previously panicked.`,
      );

      await invalidateAdminAgents();
    } catch (error) {
      setRecoverAllError(
        error instanceof Error ? error.message : String(error),
      );
    } finally {
      setIsRecoverAllLoading(false);
    }
  };

  // Polling for rescrape states
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const startPollingIfNeeded = async () => {
    const activeScrapingIds = await refreshRescrapeStates();

    if (activeScrapingIds.length > 0 && !pollingIntervalRef.current) {
      pollingIntervalRef.current = setInterval(async () => {
        const newActiveScrapingIds = await refreshRescrapeStates();
        if (newActiveScrapingIds.length === 0) {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
        }
      }, RESCRAPE_POLLING_INTERVAL);
    }
  };
  // check for pending rescrapes on page load and start polling
  useEffect(() => {
    const initializeRescrapeStates = async () => {
      try {
        const activeScrapingIds = await refreshRescrapeStates();
        if (activeScrapingIds.length > 0) {
          setTimeout(() => {
            startPollingIfNeeded();
          }, RESCRAPE_POLLING_INTERVAL);
        }
      } catch (error) {
        console.error("Error initializing rescrape states:", error);
      }
    };

    initializeRescrapeStates();

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  const refreshRescrapeStates = async () => {
    try {
      const activeScrapingIds =
        await agentEditorApi.fetchActiveScrapingStates();

      // update the isLoading state for all agents
      setRescrapeStates((prev) => {
        const newStates = { ...prev };
        Object.keys(newStates).forEach((agentId) => {
          const wasLoading = newStates[agentId].isLoading;
          const isNowLoading = activeScrapingIds.includes(agentId);

          newStates[agentId] = {
            ...newStates[agentId],
            isLoading: isNowLoading,
          };

          // If agent was loading but is no longer loading, set success
          if (wasLoading && !isNowLoading) {
            newStates[agentId].success = "Rescrape complete";
            newStates[agentId].timestamp = Date.now();
            toast.success("Rescrape complete");

            // Clear success state after 30 seconds
            setTimeout(() => {
              setRescrapeStates((prev) => ({
                ...prev,
                [agentId]: {
                  ...prev[agentId],
                  success: null,
                },
              }));
            }, 30000);
          }

          // remove scrapeStartedAt if agent is done scraping
          if (!isNowLoading) {
            newStates[agentId].scrapeStartedAt = undefined;
          }
        });
        // when page refreshes, there's no scraping state for the agent, so we need to add it
        activeScrapingIds.forEach((agentId) => {
          if (!newStates[agentId]) {
            newStates[agentId] = {
              isLoading: true,
              error: null,
              success: null,
            };
          }
        });

        return newStates;
      });

      if (activeScrapingIds.length === 0) {
        await invalidateAdminAgents();
      }

      return activeScrapingIds;
    } catch (error) {
      console.error("Error refreshing rescrape states:", error);
      return [];
    }
  };

  const updateRescrapeState = (
    agentId: string,
    updates: Partial<AdminActionState[string]>,
  ) => {
    setRescrapeStates((prev) => ({
      ...prev,
      [agentId]: {
        ...prev[agentId],
        ...updates,
      },
    }));
  };

  return (
    <AdminContext.Provider
      value={{
        invalidateAdminAgents,
        panicStates,
        recoverStates,
        rescrapeStates,
        handlePanic,
        handleRecover,
        handleRescrape,
        updateRescrapeState,
        isPanicAllLoading,
        isPanicAllConfirmationVisible,
        panicAllError,
        panicAllSuccess,
        setIsPanicAllConfirmationVisible,
        handlePanicAll,
        isRecoverAllLoading,
        recoverAllError,
        recoverAllSuccess,
        handleRecoverAll,
      }}
    >
      {children}
    </AdminContext.Provider>
  );
};
